import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { Fornecedor, FornecedorFormData } from '../../types/fornecedor';
import { formatarCPFCNPJ, formatarCPFCNPJOnInput, formatarPhone, formatarPhoneOnInput } from '../../utils/formatToView';
import EstadosBrSelect from '../selects/EstadosBrSelect';

interface FornecedorFormProps {
  open: boolean;
  onClose: () => void;
  fornecedor?: Fornecedor | null;
  onSubmit: (data: FornecedorFormData) => Promise<void>;
}

const FornecedorForm: React.FC<FornecedorFormProps> = ({
  open,
  onClose,
  fornecedor,
  onSubmit
}) => {
  const [formData, setFormData] = useState<FornecedorFormData>({
    nome: '',
    cnpj: '',
    email: '',
    telefone: '',
    endereco: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: '',
    numero: '',
    complemento: '',
    vendedor: '',
    observacoes: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (fornecedor) {
      setFormData({
        nome: fornecedor.nome || '',
        cnpj: fornecedor.cnpj || '',
        email: fornecedor.email || '',
        telefone: fornecedor.telefone || '',
        endereco: fornecedor.endereco || '',
        bairro: fornecedor.bairro || '',
        cidade: fornecedor.cidade || '',
        estado: fornecedor.estado || '',
        cep: fornecedor.cep || '',
        numero: fornecedor.numero || '',
        complemento: fornecedor.complemento || '',
        vendedor: fornecedor.vendedor || '',
        observacoes: fornecedor.observacoes || ''
      });
    } else {
      setFormData({
        nome: '',
        cnpj: '',
        email: '',
        telefone: '',
        endereco: '',
        bairro: '',
        cidade: '',
        estado: '',
        cep: '',
        numero: '',
        complemento: '',
        vendedor: '',
        observacoes: ''
      });
    }
  }, [fornecedor]);

  const handleChange = (field: keyof FornecedorFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await onSubmit(formData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar fornecedor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {fornecedor ? 'Editar Fornecedor' : 'Novo Fornecedor'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Nome */}
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                label="Nome *"
                value={formData.nome}
                onChange={(e) => handleChange('nome', e.target.value)}
                size="small"
                required
                disabled={loading}
                placeholder="Nome da empresa"
              />
            </Grid>

            {/* CNPJ */}
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="CNPJ"
                value={formatarCPFCNPJ(formData.cnpj)}
                onChange={(e) => {
                  const formatted = formatarCPFCNPJOnInput(e.target.value, 14);
                  handleChange('cnpj', formatted);
                }}
                size="small"
                disabled={loading}
                placeholder="00.000.000/0000-00"
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                size="small"
                disabled={loading}
                placeholder="email@empresa.com"
              />
            </Grid>

            {/* Telefone */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telefone *"
                value={formatarPhone(formData.telefone)}
                onChange={(e) => {
                  const formatted = formatarPhoneOnInput(e.target.value, 11);
                  handleChange('telefone', formatted);
                }}
                required
                size="small"
                disabled={loading}
                placeholder="(11) 99999-9999"
              />
            </Grid>
            {/* Vendedor */}
            <Grid item xs={12} md={12}>
               <TextField
                 fullWidth
                 label="Vendedor"
                 value={formData.vendedor}
                 onChange={(e) => handleChange('vendedor', e.target.value)}
                 size="small"
                 disabled={loading}
                 placeholder="Nome do vendedor"
               />
             </Grid>
            <Grid item xs={12} md={12}>
              <Divider />
            </Grid>

            {/* Endereço */}
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Endereço"
                value={formData.endereco}
                onChange={(e) => handleChange('endereco', e.target.value)}
                size="small"
                disabled={loading}
                placeholder="Rua, Avenida, etc."
              />
            </Grid>

            {/* Número */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Número"
                value={formData.numero}
                onChange={(e) => handleChange('numero', e.target.value)}
                size="small"
                disabled={loading}
                placeholder="123"
              />
            </Grid>

            {/* Bairro */}
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Bairro"
                value={formData.bairro}
                onChange={(e) => handleChange('bairro', e.target.value)}
                size="small"
                disabled={loading}
                placeholder="Centro, Jardim, etc."
              />
            </Grid>

            {/* CEP */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="CEP"
                value={formData.cep}
                onChange={(e) => handleChange('cep', e.target.value)}
                size="small"
                disabled={loading}
                placeholder="00000-000"
              />
            </Grid>

            {/* Cidade */}
            <Grid item xs={12} md={9}>
              <TextField
                fullWidth
                label="Cidade *"
                value={formData.cidade}
                onChange={(e) => handleChange('cidade', e.target.value)}
                required
                size="small"
                disabled={loading}
                placeholder="São Paulo"
              />
            </Grid>

            {/* Estado */}
            <Grid item xs={12} md={3}>
              <EstadosBrSelect
                label="Estado *"
                value={formData.estado}
                onChange={(e) => handleChange('estado', e.target.value)}
                required
                disabled={loading}
              />
            </Grid>

            

            {/* Complemento */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Complemento"
                value={formData.complemento}
                onChange={(e) => handleChange('complemento', e.target.value)}
                size="small"
                disabled={loading}
                placeholder="Apto, Sala, etc."
              />
            </Grid>

            {/* Observações */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observações"
                value={formData.observacoes}
                onChange={(e) => handleChange('observacoes', e.target.value)}
                size="small"
                disabled={loading}
                multiline
                rows={3}
                placeholder="Observações adicionais"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Salvando...' : (fornecedor ? 'Atualizar' : 'Criar')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FornecedorForm;
